import { useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import MissionDocumentDropzone from '@tpct/mission-core/components/MissionDocumentDropzone';
import FileListItem from '@tpct/mission-core/components/FileListItem';
import MissionDocumentPreviewModal, {
  type MissionDocumentPreviewFile,
} from '@tpct/mission-core/components/MissionDocumentPreviewModal';
import { buildDocumentFieldConfigs } from '@tpct/mission-core/utils/missionDocumentPolicy';
import type { MissionApiConfig } from '../../types/missionPortal';
import type { MissionDocumentRow, MissionDocumentType } from '@tpct/mission-core/utils/missionStartApi';
import { deleteMissionDocument, uploadMissionDocument } from '@tpct/mission-core/utils/missionStartApi';
import { errorToUserFacingMessage } from '../../api/apiErrors';

const MISSION_DOC_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type MissionDocumentFieldProps = {
  apiConfig: MissionApiConfig;
  missionId: number;
  field: {
    type: MissionDocumentType;
    title: string;
    description: string;
    placeholder: string;
  };
  existing?: MissionDocumentRow;
  readOnly: boolean;
  uploading: boolean;
  uploadDisabled: boolean;
  onUploaded: (document: MissionDocumentRow) => void;
  onDeleted: (type: MissionDocumentType) => void;
  onError: (message: string) => void;
  onUploadStart: (type: MissionDocumentType) => void;
  onUploadEnd: () => void;
  onPreview: (file: MissionDocumentPreviewFile) => void;
};

const MissionDocumentField = ({
  apiConfig,
  missionId,
  field,
  existing,
  readOnly,
  uploading,
  uploadDisabled,
  onUploaded,
  onDeleted,
  onError,
  onUploadStart,
  onUploadEnd,
  onPreview,
}: MissionDocumentFieldProps) => {
  const uploadFile = async (file: File) => {
    onUploadStart(field.type);
    onError('');
    try {
      const uploaded = await uploadMissionDocument(apiConfig, missionId, field.type, file);
      onUploaded(uploaded);
    } catch (e) {
      onError(errorToUserFacingMessage(e, __('Impossible de déposer le fichier.')));
    } finally {
      onUploadEnd();
    }
  };

  const handleDelete = async () => {
    onError('');
    try {
      await deleteMissionDocument(apiConfig, missionId, field.type);
      onDeleted(field.type);
    } catch (e) {
      onError(errorToUserFacingMessage(e, __('Impossible de supprimer le fichier.')));
    }
  };

  return (
    <div className="mission-preparation__field">
      <h3 className="mission-preparation__subtitle">{field.title}</h3>
      <p className="mission-preparation__help">{field.description}</p>
      {existing !== undefined ? (
        <FileListItem
          file={{
            id: existing.attachment_id,
            file_name: existing.file_name,
            mime_type: existing.mime_type,
          }}
          onPreview={(file) => {
            onPreview({
              id: file.id,
              file_name: file.file_name ?? existing.file_name,
              mime_type: file.mime_type ?? existing.mime_type,
              document_type: field.type,
            });
          }}
          showDelete={!readOnly}
          onDelete={() => {
            void handleDelete();
          }}
        />
      ) : (
        <MissionDocumentDropzone
          inputId={`mission-doc-${field.type}`}
          accept={MISSION_DOC_ACCEPT}
          ariaLabel={field.placeholder}
          primaryLabel={field.placeholder}
          busy={uploading}
          disabled={readOnly || uploadDisabled}
          onFileSelect={(file) => {
            void uploadFile(file);
          }}
        />
      )}
    </div>
  );
};

type MissionPreparationSectionProps = {
  apiConfig: MissionApiConfig;
  missionId: number;
  documents: MissionDocumentRow[];
  requiredDocumentTypes: MissionDocumentType[];
  readOnly: boolean;
  onDocumentsChange: (documents: MissionDocumentRow[]) => void;
  onError: (message: string) => void;
};

const MissionPreparationSection = ({
  apiConfig,
  missionId,
  documents,
  requiredDocumentTypes,
  readOnly,
  onDocumentsChange,
  onError,
}: MissionPreparationSectionProps) => {
  const [uploadingType, setUploadingType] = useState<MissionDocumentType | null>(null);
  const [previewFile, setPreviewFile] = useState<MissionDocumentPreviewFile | null>(null);
  const documentFields = buildDocumentFieldConfigs(requiredDocumentTypes);

  const findDocument = (type: MissionDocumentType): MissionDocumentRow | undefined =>
    documents.find((doc) => doc.document_type === type);

  return (
    <section className="container-style mission-page__card mission-page__card--span-3" aria-labelledby="mission-preparation-heading">
      <h2 id="mission-preparation-heading" className="h4">{__('Préparation de la mission')}</h2>
      {documentFields.map((field) => (
        <MissionDocumentField
          key={field.type}
          apiConfig={apiConfig}
          missionId={missionId}
          field={field}
          existing={findDocument(field.type)}
          readOnly={readOnly}
          uploading={uploadingType === field.type}
          uploadDisabled={uploadingType !== null}
          onUploaded={(uploaded) => {
            onDocumentsChange([
              ...documents.filter((doc) => doc.document_type !== field.type),
              uploaded,
            ]);
          }}
          onDeleted={(type) => {
            onDocumentsChange(documents.filter((doc) => doc.document_type !== type));
          }}
          onError={onError}
          onUploadStart={setUploadingType}
          onUploadEnd={() => setUploadingType(null)}
          onPreview={setPreviewFile}
        />
      ))}
      <MissionDocumentPreviewModal
        isOpen={previewFile !== null}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
        apiConfig={apiConfig}
        missionId={missionId}
      />
    </section>
  );
};

export default MissionPreparationSection;
